import { alpha, createTheme } from "@mui/material/styles";

import montserrat from "../../static/fonts/Montserrat-VariableFont_wght.ttf";


const brand = {
  canvas: "#07090e",
  canvasRaised: "#0b0f17",
  surface: "#101620",
  surfaceRaised: "#151c28",
  warmWhite: "#f9f5ee",
  text: "#f4f6fb",
  textMuted: "#a8b2c4",
  border: "#263044",
  accent: "#8eafff",
  cyan: "#62d7f3",
  violet: "#b096ff",
  emerald: "#6ed6a0",
  amber: "#efbd6a",
};

const supportAdminTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: brand.warmWhite,
      light: "#ffffff",
      dark: "#d9d4cc",
      contrastText: "#0a0c11",
    },
    secondary: {
      main: brand.accent,
      light: "#b4c9ff",
      dark: "#688fe8",
      contrastText: "#09101d",
    },
    background: {
      default: brand.canvas,
      paper: brand.surface,
    },
    text: {
      primary: brand.text,
      secondary: brand.textMuted,
      disabled: "#717b8d",
    },
    divider: alpha(brand.border, 0.9),
    success: {
      main: "#72d6a0",
      dark: "#3ea86f",
    },
    warning: {
      main: "#f4c56a",
      dark: "#bc8731",
    },
    error: {
      main: "#ff858b",
      dark: "#c94d59",
    },
    info: {
      main: brand.accent,
      dark: "#557acb",
    },
    action: {
      hover: alpha(brand.warmWhite, 0.055),
      selected: alpha(brand.accent, 0.14),
      disabled: "#717b8d",
      disabledBackground: alpha(brand.warmWhite, 0.07),
      focus: alpha(brand.accent, 0.2),
    },
  },
  shape: {
    borderRadius: 10,
  },
  spacing: 8,
  typography: {
    fontFamily: 'Montserrat, "Segoe UI", sans-serif',
    fontSize: 13,
    h1: {
      fontSize: "1.75rem",
      lineHeight: 1.22,
      fontWeight: 620,
      letterSpacing: "-0.025em",
    },
    h2: {
      fontSize: "1.25rem",
      lineHeight: 1.3,
      fontWeight: 620,
      letterSpacing: "-0.015em",
    },
    h3: {
      fontSize: "1.05rem",
      lineHeight: 1.35,
      fontWeight: 620,
    },
    h4: {
      fontSize: "1.75rem",
      lineHeight: 1.2,
      fontWeight: 620,
      fontVariantNumeric: "tabular-nums",
    },
    h5: {
      fontSize: "1.15rem",
      lineHeight: 1.35,
      fontWeight: 620,
    },
    h6: {
      fontSize: "0.96rem",
      lineHeight: 1.4,
      fontWeight: 620,
    },
    body1: {
      fontSize: "0.875rem",
      lineHeight: 1.58,
    },
    body2: {
      fontSize: "0.8125rem",
      lineHeight: 1.55,
    },
    button: {
      fontSize: "0.8125rem",
      lineHeight: 1.35,
      fontWeight: 620,
      letterSpacing: "0.005em",
      textTransform: "none",
    },
    caption: {
      fontSize: "0.72rem",
      lineHeight: 1.5,
    },
    overline: {
      fontSize: "0.68rem",
      lineHeight: 1.6,
      fontWeight: 650,
      letterSpacing: "0.105em",
      textTransform: "uppercase",
    },
  },
  shadows: [
    "none",
    "0 1px 2px rgba(0,0,0,0.28)",
    "0 8px 24px rgba(0,0,0,0.22)",
    "0 12px 30px rgba(0,0,0,0.24)",
    "0 16px 40px rgba(0,0,0,0.26)",
    "0 18px 48px rgba(0,0,0,0.28)",
    "0 20px 56px rgba(0,0,0,0.3)",
    "0 22px 60px rgba(0,0,0,0.31)",
    "0 24px 64px rgba(0,0,0,0.32)",
    "0 26px 68px rgba(0,0,0,0.33)",
    "0 28px 72px rgba(0,0,0,0.34)",
    "0 30px 76px rgba(0,0,0,0.35)",
    "0 32px 80px rgba(0,0,0,0.36)",
    "0 34px 84px rgba(0,0,0,0.37)",
    "0 36px 88px rgba(0,0,0,0.38)",
    "0 38px 92px rgba(0,0,0,0.39)",
    "0 40px 96px rgba(0,0,0,0.4)",
    "0 42px 100px rgba(0,0,0,0.41)",
    "0 44px 104px rgba(0,0,0,0.42)",
    "0 46px 108px rgba(0,0,0,0.43)",
    "0 48px 112px rgba(0,0,0,0.44)",
    "0 50px 116px rgba(0,0,0,0.45)",
    "0 52px 120px rgba(0,0,0,0.46)",
    "0 54px 124px rgba(0,0,0,0.47)",
    "0 56px 128px rgba(0,0,0,0.48)",
  ],
  transitions: {
    duration: {
      shortest: 100,
      shorter: 140,
      short: 170,
      standard: 210,
      complex: 260,
      enteringScreen: 190,
      leavingScreen: 160,
    },
  },
  components: {
    MuiScopedCssBaseline: {
      styleOverrides: {
        root: {
          minHeight: "100vh",
          backgroundColor: brand.canvas,
          color: brand.text,
          colorScheme: "dark",
          "@font-face": {
            fontFamily: "Montserrat",
            fontStyle: "normal",
            fontDisplay: "swap",
            fontWeight: "100 900",
            src: `url(${montserrat}) format("truetype")`,
          },
          "*": {
            scrollbarWidth: "thin",
            scrollbarColor: `${brand.border} ${brand.canvasRaised}`,
          },
          "@media (prefers-reduced-motion: reduce)": {
            "*, *::before, *::after": {
              scrollBehavior: "auto !important",
              transitionDuration: "0.01ms !important",
              animationDuration: "0.01ms !important",
              animationIterationCount: "1 !important",
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(brand.canvasRaised, 0.94),
          backgroundImage: "none",
          borderBottom: `1px solid ${alpha(brand.border, 0.9)}`,
          backdropFilter: "blur(16px)",
          boxShadow: "none",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: "auto 0 -1px",
            height: 1,
            backgroundColor: alpha(brand.accent, 0.38),
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        outlined: {
          borderColor: alpha(brand.border, 0.9),
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: brand.surface,
          backgroundImage: `linear-gradient(135deg, ${alpha(brand.accent, 0.04)}, ${alpha(brand.accent, 0.015)})`,
          border: `1px solid ${alpha(brand.border, 0.92)}`,
          boxShadow: "0 6px 20px rgba(0,0,0,0.16)",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 18,
          "&:last-child": {
            paddingBottom: 18,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: "small",
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minHeight: 38,
          borderRadius: 8,
          paddingInline: 15,
          "@media (max-width: 600px)": {
            minHeight: 44,
          },
          "&.Mui-focusVisible": {
            outline: `2px solid ${brand.accent}`,
            outlineOffset: 2,
          },
        },
        contained: {
          boxShadow: "0 5px 16px rgba(0,0,0,0.22)",
        },
        outlined: {
          borderColor: alpha(brand.textMuted, 0.45),
          "&:hover": {
            borderColor: alpha(brand.warmWhite, 0.72),
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          "@media (max-width: 600px)": {
            width: 44,
            height: 44,
          },
          "&.Mui-focusVisible": {
            outline: `2px solid ${brand.accent}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: "small",
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 8,
          "@media (max-width: 600px)": {
            minHeight: 44,
          },
          backgroundColor: alpha(brand.canvas, 0.42),
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(brand.textMuted, 0.32),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(brand.textMuted, 0.58),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: brand.accent,
            borderWidth: 1,
          },
          "&.Mui-disabled": {
            backgroundColor: alpha(brand.warmWhite, 0.035),
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.82rem",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          minWidth: 0,
          borderRadius: 8,
          color: brand.textMuted,
          alignItems: "flex-start",
          justifyContent: "flex-start",
          textTransform: "none",
          fontSize: "0.82rem",
          fontWeight: 560,
          "&.Mui-selected": {
            color: brand.text,
            backgroundColor: alpha(brand.accent, 0.13),
            backgroundImage: `linear-gradient(90deg, ${alpha(brand.accent, 0.13)}, ${alpha(brand.accent, 0.07)})`,
            boxShadow: `inset 0 0 0 1px ${alpha(brand.accent, 0.08)}`,
          },
          "&.Mui-focusVisible": {
            outline: `2px solid ${brand.accent}`,
            outlineOffset: -2,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: brand.accent,
          borderRadius: 4,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: alpha(brand.border, 0.72),
          padding: "11px 14px",
          fontSize: "0.79rem",
          verticalAlign: "middle",
        },
        head: {
          color: brand.textMuted,
          backgroundColor: brand.canvasRaised,
          backgroundImage: `linear-gradient(90deg, ${alpha(brand.accent, 0.03)}, ${alpha(brand.accent, 0.015)})`,
          fontSize: "0.68rem",
          fontWeight: 680,
          letterSpacing: "0.055em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.MuiTableRow-hover:hover": {
            backgroundColor: alpha(brand.accent, 0.065),
          },
          "&.Mui-focusVisible, &:focus-visible": {
            outline: `2px solid ${brand.accent}`,
            outlineOffset: -2,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${alpha(brand.border, 0.72)}`,
          color: brand.textMuted,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 25,
          borderRadius: 6,
          fontSize: "0.7rem",
          fontWeight: 650,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          border: "1px solid",
          borderColor: "currentColor",
          borderRadius: 8,
          backgroundImage: "none",
        },
        standardError: {
          backgroundColor: alpha("#ff858b", 0.08),
        },
        standardWarning: {
          backgroundColor: alpha("#f4c56a", 0.08),
        },
        standardInfo: {
          backgroundColor: alpha(brand.accent, 0.08),
        },
        standardSuccess: {
          backgroundColor: alpha("#72d6a0", 0.08),
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: `1px solid ${alpha(brand.border, 0.95)}`,
          backgroundColor: brand.surfaceRaised,
          boxShadow: "0 28px 80px rgba(0,0,0,0.48)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          fontWeight: 650,
          padding: "18px 22px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "20px 22px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          gap: 8,
          padding: "14px 22px 20px",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${alpha(brand.border, 0.95)}`,
          backgroundColor: brand.surfaceRaised,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#202838",
          color: brand.text,
          border: `1px solid ${alpha(brand.textMuted, 0.2)}`,
          fontSize: "0.7rem",
        },
      },
    },
  },
});

export { brand };
export default supportAdminTheme;
